import React, { Fragment, useContext, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { API, handleError, postRequestConfig } from './api';
import FormTextInput from './FormTextInput';
import { validateEmail } from './Register';
import UserSettingsContext from './UserSettingsContext';
import "./form.css";

function EmailLogIn() {
  const { t } = useTranslation("translations");

  const location = useLocation();
  const history = useHistory();
  const { userSettings, setUserSettings } = useContext(UserSettingsContext);

  const { state = {} } = location;

  const [processing, setProcessing] = useState(false);

  const [emailValid, setEmailValid] = useState(undefined);
  const [passwordValid, setPasswordValid] = useState(undefined);
  const [error, setError] = useState(undefined);

  const emailRef = useRef("");
  const passwordRef = useRef("");
  const active = useRef(true);

  const { redirect, signupToken } = userSettings;
  const redirectTo = state.redirect || redirect || "/registered";

  function validatePassword(value) {
    if (!value) {
      return undefined;
    }
    return value.length >= 8;
  }

  function getEmailValidation(value) {
    setError(undefined);
    const valid = validateEmail(value);
    setEmailValid(valid);
    return valid ? undefined : t('Please check the spelling of your email address.');
  }

  function getPasswordValidation(value) {
    setError(undefined);
    const valid = validatePassword(value);
    setPasswordValid(valid);
    return valid ? undefined : t('Password must be at least 8 characters long.');
  }

  function processForm(event) {
    event.preventDefault();
    if (processing) {
      return;
    }
    const email = emailRef.current.value;
    const password = passwordRef.current.value;
    setProcessing(true);
    fetch(`${API}/login/email`, {
      ...postRequestConfig,
      body: `address=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}${
        signupToken && !redirectTo.match(/(\?token=|&token=)([^&]+)/) ? `&token=${encodeURIComponent(signupToken)}` : ""
      }&redirect=${encodeURIComponent(redirectTo)}`,
    })
      .then(handleError)
      .then(() => {
        if (!active.current) {
          // We have been unmounted already
          return;
        }
        setUserSettings({
          ...userSettings,
          email,
        });
        setProcessing(false);
        if (redirectTo[0] !== "/") {
          // Not a local route
          document.location = redirectTo;
        } else {
          history.push(redirectTo);
        }
      })
      .catch((error) => {
        if (!active.current) {
          // We have been unmounted already
          return;
        }
        if (error.response && error.response.status === 404) {
          // User not found
          setError(t("Login failed. Please check your email address and password."));
        } else {
          console.error(error);
          setError(t("Login failed. Please try again."));
        }
        setProcessing(false);
      });
  }

  return (
    <Fragment>
      <header>
        <h1>{t("Log In!")}</h1>
      </header>
      <main>
        <form onSubmit={processForm}>
          <FormTextInput
            ref={emailRef}
            label={t('Email address')}
            id="email"
            type="email"
            name="email"
            defaultValue={state.email || ""}
            autoFocus
            required
            title={t('Your email address acts as a username.')}
            placeholder=""
            getValidationError={getEmailValidation}
          />
          <FormTextInput
            ref={passwordRef}
            label={t('Password')}
            id="password"
            type="password"
            name="new-password"
            defaultValue=""
            required
            title={t('Your password for Sensotrend services.')}
            pattern=".{8,}"
            placeholder=""
            getValidationError={getPasswordValidation}
          />
          <input type="submit" disabled={processing || !emailValid || !passwordValid} value={t('OK')} />
        </form>
        {error ? (
          <p className="error">
            <strong>{error}</strong>
          </p>
        ) : undefined}
        <p>
          {t('Have you forgotten your password?')}{" "}
          <Link to={() => ({ pathname: "/reset", state: { email: emailRef.current.value } })}>
            {t('Reset password')}
          </Link>
        </p>
        <p>
          {t('Don\'t have a Sensotrend account?')}{" "}
          <Link to={() => ({ pathname: "/signup", state: { email: emailRef.current.value } })}>
            {t('Create a new account')}
          </Link>
        </p>
      </main>
    </Fragment>
  );
}

export default EmailLogIn;
